// ─────────────────────────────────────────────────────────────────────────────
// ServiceTypeChip — the record's service type (trade) as a small glass chip.
//
// Neutral on purpose: pastels mean phases of work (lib/phaseTone), so a
// lavender "Painting" chip would read as a lead. The trade is told by its
// name. A record with no service type renders a muted, dashed
// "No service type" chip so it reads as unassigned rather than missing.
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties } from 'react'

interface ServiceTypeChipProps {
  /** Display name; null/undefined renders the unassigned state. */
  name: string | null | undefined
  /** The category's pastel token. Accepted for API stability; not drawn —
   *  see the note above. */
  color?: string | null | undefined
  size?: 'xs' | 'sm'
  style?: CSSProperties
}

export function ServiceTypeChip({ name, size = 'sm', style }: ServiceTypeChipProps) {
  const height = size === 'xs' ? 20 : 24
  const fontSize = size === 'xs' ? 11 : 12
  const assigned = Boolean(name)

  return (
    <span
      title={assigned ? `Service type: ${name}` : 'No service type'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height,
        padding: size === 'xs' ? '0 8px' : '0 10px',
        maxWidth: '100%',
        minWidth: 0,
        borderRadius: 9999,
        fontSize,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
        ...(assigned
          ? {
              background: 'rgb(var(--fg-rgb) / 0.06)',
              border: '0.5px solid rgb(var(--fg-rgb) / 0.12)',
              color: 'rgb(var(--fg-rgb) / 0.72)',
            }
          : {
              background: 'transparent',
              border: '0.5px dashed rgb(var(--fg-rgb) / 0.18)',
              color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))',
            }),
        ...style,
      }}
    >
      {!assigned && (
        <span
          aria-hidden
          style={{ width: 7, height: 7, borderRadius: 9999, flexShrink: 0, border: '1px solid rgb(var(--fg-rgb) / 0.30)', boxSizing: 'border-box' }}
        />
      )}
      {assigned && <span className="sr-only">Service type: </span>}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {assigned ? name : 'No service type'}
      </span>
    </span>
  )
}
